import { PureComponent, ReactNode } from 'react';
import { batch, connect, useDispatch, useSelector } from 'react-redux';
import { DatePicker, Table, Input } from 'antd';
const { Column } = Table;
import { DeleteOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import Fuse from 'fuse.js';

import { getLabVitals, getPtPastVitals } from '../../../../store/selectors/prescriptionPad';
import { LabVitalEntity } from '../../../../store/types/prescription';
import TableWithDragableRows, { DragHandle } from './padV2/TableWithDragableRows';
import { EditableCell, EditableCellV2 } from './padV2/MedTableRenderer';
import fetchMatchingLabVitals, { LabVital } from '../../../../api/fetchMatchingLabVitals';
import { buildOptions } from './padV2/SymDiaTableV2';
import { toPxLabVitalFromApiObj } from './padV2/LabVitalAutoSuggest';
import { track } from '../../../../tracking';
import {
    copyLabVitalDate,
    deleteLabVital,
    showDialog,
    swapPxElement,
    upsertLabVital,
} from '../../../../store/actions/prescriptionPad';
import AntSelect from './padV2/AntSelect';
import { PAD_V2_CONTAINER } from './PadContainer';
import { LabVital as LabVitalIcon } from '../../../common/Icons';
import {
    INTERPOLATION_VALUES,
    INTERPOLATION_VALUES_FOR_PANELS,
    MAX_G_LAB_VITALS,
} from '../../../../constants';
import { columnHeaderContent, headerStyle } from '../../../common/TableUtils';
import { GlobalState } from '../../../../store/reducers';
import CopyToAll from './padV2/CopyToAll';
import { GLabVitalEntity } from '../../../../store/db';
import { getG } from '../../../../store/idbHandler';
import { DB_TABLES } from '../../../../store/dbTables';
import { EkaFocosableElement } from '../../../common/types/EkaFocusable';
import { getMyDbId } from '../../../../api/mactiveid';

type Props = {
    elements: ReadonlyArray<LabVitalEntity>;
    containerId: string;
    showLongitudinalView: () => void;
    historicalDataIdList?: string[];

    onElementSelect: (index: number, option?: LabVital | LabVitalEntity) => void;
    onElementUpdate: (option: LabVitalEntity, index: number) => void;
    onElementDelete: (id: string, index: number) => void;
    onCopyDate: (index: number) => void;
    onDrag: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
};

const fuseConfig = { keys: ['name'], threshold: 0.2 };

class LabVitalTable extends PureComponent<Props> {
    static REF_MAP: EkaFocosableElement[][] = [];
    fuse = new Fuse([] as GLabVitalEntity[], fuseConfig);
    gElements: GLabVitalEntity[] | undefined;

    componentDidMount = async (): Promise<void> => {
        const gPromise = getG<GLabVitalEntity>(
            getMyDbId() || '',
            DB_TABLES['labVitals'],
            MAX_G_LAB_VITALS,
        );

        Promise.all([gPromise]).then((values) => {
            this.fuse = new Fuse(values[0], fuseConfig);
            this.gElements = values[0];
        });
    };

    TABLE_ID = 'lab-vital-table';

    getStrValueAtIndex = (element: LabVitalEntity, index: number): string | undefined => {
        switch (index) {
            case 0:
                return element.id ? element.name : undefined;
            case 1:
                return element.unit?.name;
            case 2:
                return element.value;
            case 3:
                return element.interpretation?.value;
            case 4:
                return element.date;
            case 5:
                return element.remark;
        }

        return '';
    };

    handleNextFocus = (rowIndex: number, columnIndex: number, isForce?: boolean): void => {
        const { elements } = this.props;

        const element = elements[rowIndex];

        if (!element) {
            return;
        }

        let didTheJob = false;

        if (columnIndex < 6) {
            if (!isForce) {
                const valueAtCurrentIndex = this.getStrValueAtIndex(
                    element,

                    columnIndex,
                );
                if (!valueAtCurrentIndex) {
                    // value is cleared
                    didTheJob = true;
                    return;
                }
            }

            for (let j = columnIndex + 1; j < 8; j++) {
                const nextIndex = columnIndex === 3 ? 5 : j;
                if (LabVitalTableComponent.REF_MAP[rowIndex][nextIndex]) {
                    LabVitalTableComponent.REF_MAP[rowIndex][nextIndex]?.focus();
                    didTheJob = true;
                    break;
                }
            }
        }
    };

    findIndexForRecordKey = (key: string): number => {
        return this.props.elements.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    elementSuggestApi = async (text: string): Promise<LabVital[]> => {
        try {
            const res = await fetchMatchingLabVitals(text);
            return res || [];
        } catch (e) {
            track('ERROR', { on: 'LabVitalTable#elementSuggestApi', error: (e as Error).message });
            return [];
        }
    };

    handleSearch = async (value: string): Promise<LabVital[]> => {
        const { elements } = this.props;

        const all = await buildOptions<LabVital>(
            value,
            elements,
            this.gElements || [],
            this.elementSuggestApi,
            this.fuse,
            undefined,
            true,
        );

        return all;
    };

    nameHeaderContent = columnHeaderContent('Investigation name');
    unitHeaderContent = columnHeaderContent('Unit');
    readingHeaderContent = columnHeaderContent('READING/observations');
    interpretationHeaderContent = columnHeaderContent('InterpreTation');
    dateHeaderContent = columnHeaderContent('Date');
    rangeHeaderContent = columnHeaderContent('Ref. Range');
    remarkHeaderContent = columnHeaderContent('ADDITIONAL NOTES');

    OptionIcon = LabVitalIcon('fill-darwin-accent-bright-yellow-800');

    renderCellAutosuggestItem = (option: LabVitalEntity) => {
        return (
            <RenderCellAutosuggestItemJsx
                option={option}
                wrapperClassName={'lx-reading-dropdown-item'}
                icon={
                    option.is_panel ? (
                        <div className="font-700 text-darwin-accent-bright-yellow-800">P</div>
                    ) : (
                        this.OptionIcon
                    )
                }
                iconWrapperClassName={'circle-lx-reading'}
            />
        );
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!LabVitalTableComponent.REF_MAP[elIndex]) {
            LabVitalTableComponent.REF_MAP[elIndex] = [];
        }

        LabVitalTableComponent.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    showLongitudinalView = (id: string) => {
        const { showLongitudinalView } = this.props;
        showLongitudinalView();

        setTimeout(() => {
            const el = document.getElementsByClassName(`pt-last-vital-${id}`)[0] as HTMLElement;
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
                el.style.backgroundColor = 'rgba(255, 250, 239, 1)';
                if (el.children[0]) {
                    (el.children[0] as HTMLElement).style.setProperty(
                        'background',
                        'rgba(255, 250, 239, 1)',
                        'important',
                    );
                }
            }
        }, 1000);
    };

    renderName = (_: any, __: LabVitalEntity, index: number) => {
        const { elements, historicalDataIdList, containerId } = this.props;

        return {
            children: (
                <div className="flex space-x-16 items-center">
                    <DragHandle />
                    <div className="font-700 w-full text-neutral-black text-14 placeholder-neutral-6">
                        <EditableCell
                            fieldName={`sdn-${index}`}
                            label={'Lab vital'}
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, index, 0);
                            }}
                            debounceDelay={200}
                            customDropdownClass={`py-8 lx-reading-dropdown rounded-8`}
                            customDropdownWidth={350}
                            optionsRenderer={this.renderCellAutosuggestItem}
                            onSearch={this.handleSearch}
                            getLabelToCompare={(o) => o.name}
                            selectedValue={
                                elements[index] && elements[index].id ? elements[index] : undefined
                            }
                            onSelect={(option) => {
                                this.props.onElementSelect(index, option ? option : undefined);
                            }}
                            containerId={containerId}
                            placeholder={`Type property`}
                        />
                    </div>
                    {historicalDataIdList &&
                        historicalDataIdList.find((id) => id === elements[index].id) && (
                            <div
                                onClick={() => this.showLongitudinalView(elements[index].id)}
                                className="bg-transparent transition-all hover:bg-neutral-10 rounded-4 flex items-center justify-center cursor-pointer w-20 h-20 tooltip-wrapper min-w-20 min-h-20"
                            >
                                <svg
                                    width="12"
                                    height="20"
                                    viewBox="0 0 12 11"
                                    fill="fill-neutral-900"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M1.875 4.375H0.375C0.164062 4.375 0 4.5625 0 4.75V6.25C0 6.46094 0.164062 6.625 0.375 6.625H1.875C2.0625 6.625 2.25 6.46094 2.25 6.25V4.75C2.25 4.5625 2.0625 4.375 1.875 4.375ZM1.875 8.125H0.375C0.164062 8.125 0 8.3125 0 8.5V10C0 10.2109 0.164062 10.375 0.375 10.375H1.875C2.0625 10.375 2.25 10.2109 2.25 10V8.5C2.25 8.3125 2.0625 8.125 1.875 8.125ZM1.875 0.625H0.375C0.164062 0.625 0 0.8125 0 1V2.5C0 2.71094 0.164062 2.875 0.375 2.875H1.875C2.0625 2.875 2.25 2.71094 2.25 2.5V1C2.25 0.8125 2.0625 0.625 1.875 0.625ZM11.4375 4.9375H4.28906C3.98438 4.9375 3.75 5.19531 3.75 5.5C3.75 5.82812 3.98438 6.0625 4.28906 6.0625H11.4375C11.7422 6.0625 12 5.82812 12 5.5C12 5.19531 11.7422 4.9375 11.4375 4.9375ZM11.4375 1.1875H4.28906C3.98438 1.1875 3.75 1.44531 3.75 1.75C3.75 2.03125 3.98438 2.3125 4.28906 2.3125H11.4375C11.7422 2.3125 12 2.07812 12 1.77344C12 1.49219 11.7422 1.1875 11.4375 1.1875ZM11.4375 8.6875H4.28906C3.98438 8.6875 3.75 8.94531 3.75 9.25C3.75 9.57812 3.98438 9.8125 4.28906 9.8125H11.4375C11.7422 9.8125 12 9.57812 12 9.25C12 8.94531 11.7422 8.6875 11.4375 8.6875Z" />
                                </svg>
                                <div className="tooltip-tip top">See Historical Data</div>
                            </div>
                        )}
                </div>
            ),
        };
    };

    renderRemark = (_: any, el: LabVitalEntity, index: number) => {
        const value = el.remark || '';

        return {
            children: (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        defaultValue={value}
                        onBlur={(e) => {
                            this.props.onElementUpdate({ ...el, remark: e.target.value }, index);
                        }}
                        setRef={(ref) => {
                            this.setPropertyInputRef(ref, index, 5);
                            return;
                        }}
                        label="Add notes here"
                    >
                        <Input.TextArea
                            autoFocus
                            defaultValue={value}
                            rows={2}
                            placeholder="Add notes here"
                        />
                    </EditableCellV2>
                </div>
            ),
        };
    };

    renderReading = (_: any, el: LabVitalEntity, index: number) => {
        const value = el.value || '';
        if (el.is_panel) {
            return <div className="px-12">-</div>;
        }

        return {
            children: (
                <EditableCellV2
                    defaultValue={value}
                    label="Reading"
                    setRef={(ref) => {
                        ref && this.setPropertyInputRef(ref, index, 2);
                        return;
                    }}
                    onBlur={(e) => {
                        this.props.onElementUpdate({ ...el, value: e.target.value }, index);
                    }}
                >
                    <Input
                        autoFocus
                        defaultValue={value}
                        placeholder="Reading"
                        allowClear
                        onPressEnter={(e) => {
                            e.currentTarget.blur();
                        }}
                    />
                </EditableCellV2>
            ),
        };
    };

    renderUnit = (_: any, el: LabVitalEntity, index: number) => {
        const { containerId } = this.props;
        if (el.is_panel) {
            return <div className="px-12">-</div>;
        }
        return {
            children: (
                <EditableCellV2
                    defaultValue={el.unit?.name || '-'}
                    label={'unit'}
                    setRef={(ref) => {
                        this.setPropertyInputRef(ref, index, 1);
                    }}
                >
                    {!el.all_units || el.all_units.length === 0 ? (
                        <p className="mx-12">{el.unit?.name || '-'}</p>
                    ) : (
                        <AntSelect
                            containerIdToAttachDropdown={containerId}
                            className="bg-transparent"
                            showArrow={false}
                            defaultOpen
                            style={{ width: '100%' }}
                            autoFocus
                            allowClear={false}
                            placeholder={'unit'}
                            optionFilterProp="children"
                            customDropdownWidth={180}
                            options={el.all_units || []}
                            disabled={!el.all_units || el.all_units.length === 0}
                            setValue={(option) => {
                                this.props.onElementUpdate(
                                    {
                                        ...el,
                                        unit: option ? option : undefined,
                                    },
                                    index,
                                );
                                this.handleNextFocus(index, 1, option ? true : false);
                            }}
                            filterOption={true}
                            value={el.unit || undefined}
                            getValue={(o) => o?.id}
                            getLabelToCompare={(o) => o.name}
                            notFoundContent={null}
                        />
                    )}
                </EditableCellV2>
            ),
        };
    };

    renderInterpretation = (_: any, el: LabVitalEntity, index: number) => {
        const { containerId } = this.props;
        const InterpolationValues = el.is_panel
            ? INTERPOLATION_VALUES_FOR_PANELS
            : INTERPOLATION_VALUES;
        const label = el.is_panel ? 'Normal/Abnormal' : 'High/Normal/Low';

        return {
            children: (
                <EditableCellV2
                    defaultValue={el.interpretation?.value || ''}
                    label={label}
                    setRef={(ref) => this.setPropertyInputRef(ref, index, 3)}
                >
                    <AntSelect
                        containerIdToAttachDropdown={containerId}
                        className="bg-transparent"
                        showArrow={false}
                        defaultOpen
                        style={{ width: '100%' }}
                        autoFocus
                        allowClear
                        placeholder={label}
                        optionFilterProp="children"
                        options={InterpolationValues}
                        setValue={(option) => {
                            this.props.onElementUpdate(
                                {
                                    ...el,
                                    interpretation: option ? option : undefined,
                                },
                                index,
                            );
                            this.handleNextFocus(index, 3, option !== undefined);
                        }}
                        filterOption={true}
                        value={el.interpretation || undefined}
                        getValue={(o) => o?.id}
                        getLabelToCompare={(o) => o.value}
                    />
                </EditableCellV2>
            ),
        };
    };

    disabledFutureDate = (current: Moment): boolean => {
        const customDate = moment().add(1, 'day').format('YYYY-MM-DD');
        return current && current > moment(customDate, 'YYYY-MM-DD');
    };

    copyDateId = (index: number): string => `copy-date-v2-svg-${index}`;

    renderDate = (_: any, el: LabVitalEntity, index: number) => {
        return {
            children: (
                <div>
                    <EditableCellV2
                        defaultValue={el.date ? moment(el.date).format("DD MMM'YY") : ''}
                        label={'Date'}
                        setRef={(ref) => this.setPropertyInputRef(ref, index, 4)}
                    >
                        <DatePicker
                            picker={'date'}
                            onChange={(t) =>
                                this.props.onElementUpdate(
                                    {
                                        ...el,
                                        date: t?.toISOString(),
                                    },
                                    index,
                                )
                            }
                            allowClear={false}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            value={el.date ? moment(el.date) : null}
                            disabledDate={this.disabledFutureDate}
                            format="DD MMM'YY"
                            getPopupContainer={() =>
                                this.props.containerId
                                    ? (document.getElementById(
                                          this.props.containerId,
                                      ) as HTMLElement)
                                    : document.body
                            }
                        />
                    </EditableCellV2>

                    <CopyToAll
                        id={this.copyDateId(index)}
                        onClick={() => this.props.onCopyDate(index)}
                        tableId={this.TABLE_ID}
                    />
                </div>
            ),
        };
    };

    rowKeyFromId = (id: string, index?: number): string =>
        `${'lab-vital'}-${index}-${(this.props.historicalDataIdList || []).length > 0}`;

    render(): ReactNode {
        const { elements, onDrag } = this.props;
        if (elements.length === 0) {
            return null;
        }
        return (
            <TableWithDragableRows
                id={this.TABLE_ID}
                showHeader
                tableLayout="fixed"
                className="lx-reading-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={elements}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={onDrag}
                scroll={{
                    x: 840,
                }}
            >
                <Column
                    title={this.nameHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-name"
                    className="lab-vital-row-name"
                    render={this.renderName}
                    fixed={true}
                />
                <Column
                    title={this.unitHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-unit"
                    className="lab-vital-row-unit"
                    render={this.renderUnit}
                />
                <Column
                    title={this.readingHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-reading"
                    className="lab-vital-row-reading"
                    render={this.renderReading}
                />
                <Column
                    title={this.interpretationHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-interpretation"
                    className="lab-vital-row-interpretation"
                    render={this.renderInterpretation}
                />
                <Column
                    title={this.dateHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-date"
                    className="lab-vital-row-date"
                    render={this.renderDate}
                />
                <Column
                    title={this.remarkHeaderContent}
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-remark"
                    className={`p-5 lab-vital-row-remark`}
                    render={this.renderRemark}
                />
                <Column
                    onHeaderCell={headerStyle}
                    key="lab-vital-row-delete"
                    className={`p-5 lab-vital-row-delete`}
                    fixed="right"
                    render={(_: any, el: LabVitalEntity, index: number) => {
                        return {
                            children: (
                                <div className="flex space-x-8 items-center justify-center">
                                    <DeleteOutlined
                                        style={{ color: '#616A8F' }}
                                        size={24}
                                        onClick={() => this.props.onElementDelete(el.id, index)}
                                        onPointerLeaveCapture={undefined}
                                    />
                                </div>
                            ),
                        };
                    }}
                />
            </TableWithDragableRows>
        );
    }
}

export const LabVitalTableComponent = connect((state: GlobalState) => ({
    historicalDataIdList: Object.keys(getPtPastVitals(state) || {}),
}))(LabVitalTable);

const PadV2LabVitalTable: React.FC = () => {
    const dispatch = useDispatch();
    const elements = useSelector(getLabVitals);

    const onElementSelect = (index: number, option?: LabVital) => {
        const data = option || { id: '', name: '' };
        dispatch(upsertLabVital(toPxLabVitalFromApiObj(data), index));
    };

    const onElementUpdate = (option: LabVitalEntity, index: number) => {
        dispatch(upsertLabVital(option, index));
    };

    const onElementDelete = (id: string, index?: number) => {
        dispatch(deleteLabVital(id, index));
    };

    const onCopyDate = (index: number) => {
        const element = elements[index];

        if (!element.date) {
            return;
        }

        dispatch(copyLabVitalDate(element.date));
    };

    const onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        if (oldIndex !== newIndex) {
            dispatch(swapPxElement('labVitals', oldIndex, newIndex));
        }
    };

    const showLongitudinalView = () => {
        dispatch(showDialog('longitudinal-vital-view'));
    };

    return (
        <LabVitalTableComponent
            onElementUpdate={onElementUpdate}
            onElementDelete={onElementDelete}
            onElementSelect={onElementSelect}
            elements={elements}
            containerId={PAD_V2_CONTAINER}
            onDrag={onDrag}
            showLongitudinalView={showLongitudinalView}
            onCopyDate={onCopyDate}
        />
    );
};

export default PadV2LabVitalTable;

export const RenderCellAutosuggestItemJsx: React.FC<{
    option: { name: string };
    wrapperClassName?: string;
    iconWrapperClassName?: string;
    icon: JSX.Element | string;
}> = ({ option, wrapperClassName, icon, iconWrapperClassName }) => {
    return (
        <div
            className={`flex items-center justify-between ${wrapperClassName} cursor-pointer font-lato w-full`}
        >
            <div className="flex items-center space-x-8">
                <div className={iconWrapperClassName}>
                    <p>{icon}</p>
                </div>
                <p className="text-neutral-1 text-14 w-full">{option.name}</p>
            </div>
        </div>
    );
};
